import { computeInvoiceTotals, DEFAULT_STRIPS_PER_BOX } from './zovcurInvoiceStore';
import { buildPeriodKey, calcReachPercent, getInvoiceBillDate } from './salesPerformanceUtils';

export const getPeriodMeta = (periodType, refDate = new Date()) => {
  const year = refDate.getFullYear();
  const month = refDate.getMonth() + 1;
  const quarter = Math.floor(refDate.getMonth() / 3) + 1;

  if (periodType === 'weekly') {
    const day = refDate.getDay();
    const diffToMonday = day === 0 ? -6 : 1 - day;
    const weekStart = new Date(refDate);
    weekStart.setHours(0, 0, 0, 0);
    weekStart.setDate(refDate.getDate() + diffToMonday);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    weekEnd.setHours(23, 59, 59, 999);

    const prevWeekStart = new Date(weekStart);
    prevWeekStart.setDate(weekStart.getDate() - 7);
    const prevWeekEnd = new Date(weekEnd);
    prevWeekEnd.setDate(weekEnd.getDate() - 7);

    return { year, month, quarter, weekStart, weekEnd, prevWeekStart, prevWeekEnd };
  }

  if (periodType === 'monthly') {
    const prevMonthDate = new Date(year, refDate.getMonth() - 1, 1);
    return {
      year,
      month,
      quarter,
      prevYear: prevMonthDate.getFullYear(),
      prevMonth: prevMonthDate.getMonth() + 1
    };
  }

  if (periodType === 'quarterly') {
    const prevQuarter = quarter === 1 ? 4 : quarter - 1;
    const prevYear = quarter === 1 ? year - 1 : year;
    return { year, month, quarter, prevYear, prevQuarter };
  }

  return { year, month, quarter, prevYear: year - 1 };
};

export const dateInRange = (date, start, end) => {
  if (!date || !start || !end) return false;
  const time = date.getTime();
  return time >= start.getTime() && time <= end.getTime();
};

export const invoiceInPeriod = (invoice, periodType, meta) => {
  const date = getInvoiceBillDate(invoice);
  if (!date) return false;

  if (periodType === 'weekly') {
    return dateInRange(date, meta.weekStart, meta.weekEnd);
  }

  if (periodType === 'monthly') {
    return date.getFullYear() === meta.year && date.getMonth() + 1 === meta.month;
  }

  if (periodType === 'quarterly') {
    const q = Math.floor(date.getMonth() / 3) + 1;
    return date.getFullYear() === meta.year && q === meta.quarter;
  }

  return date.getFullYear() === meta.year;
};

export const invoiceInPreviousPeriod = (invoice, periodType, meta) => {
  const date = getInvoiceBillDate(invoice);
  if (!date) return false;

  if (periodType === 'weekly') {
    return dateInRange(date, meta.prevWeekStart, meta.prevWeekEnd);
  }

  if (periodType === 'monthly') {
    return date.getFullYear() === meta.prevYear && date.getMonth() + 1 === meta.prevMonth;
  }

  if (periodType === 'quarterly') {
    const q = Math.floor(date.getMonth() / 3) + 1;
    return date.getFullYear() === meta.prevYear && q === meta.prevQuarter;
  }

  return date.getFullYear() === meta.prevYear;
};

const getTransactionDate = (transaction) => {
  const raw = transaction?.date || transaction?.createdAt;
  if (!raw) return null;
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const dateInPeriod = (date, periodType, meta) => {
  if (!date || Number.isNaN(date.getTime())) return false;

  if (periodType === 'weekly') {
    return dateInRange(date, meta.weekStart, meta.weekEnd);
  }

  if (periodType === 'monthly') {
    return date.getFullYear() === meta.year && date.getMonth() + 1 === meta.month;
  }

  if (periodType === 'quarterly') {
    const q = Math.floor(date.getMonth() / 3) + 1;
    return date.getFullYear() === meta.year && q === meta.quarter;
  }

  return date.getFullYear() === meta.year;
};

export const stockInPeriod = (transaction, periodType, meta) => {
  const date = getTransactionDate(transaction);
  if (!date) return false;
  return dateInPeriod(date, periodType, meta);
};

const sumInvoiceMetrics = (invoices) => {
  let revenue = 0;
  let strips = 0;
  let paidCount = 0;
  let unpaidCount = 0;
  let paidAmount = 0;
  let documentedCount = 0;

  invoices.forEach((invoice) => {
    const totals = computeInvoiceTotals(invoice?.products || []);
    revenue += totals.roundedNetAmount;
    strips += totals.quantity;

    if ((invoice.paymentStatus || 'Unpaid') === 'Paid') {
      paidCount += 1;
      paidAmount += totals.roundedNetAmount;
    } else {
      unpaidCount += 1;
    }

    const hasDoctor = Boolean(String(invoice?.customer?.doctor || '').trim());
    const hasSalesman = Boolean(String(invoice?.invoiceDetails?.salesmanName || '').trim());
    if (hasDoctor && hasSalesman) documentedCount += 1;
  });

  return {
    invoiceCount: invoices.length,
    revenue,
    strips,
    paidCount,
    unpaidCount,
    paidAmount,
    documentedCount,
    avgOrderValue: invoices.length ? revenue / invoices.length : 0,
    collectionRate: revenue ? (paidAmount / revenue) * 100 : 0,
    documentationRate: invoices.length ? (documentedCount / invoices.length) * 100 : 0
  };
};

const sumTargetsForPeriod = (targets, periodType, meta) => {
  const periodKey =
    periodType === 'monthly'
      ? buildPeriodKey('monthly', meta.year, meta.month)
      : periodType === 'quarterly'
        ? buildPeriodKey('quarterly', meta.year, null, meta.quarter)
        : buildPeriodKey('yearly', meta.year);

  const matched = targets.filter(
    (target) => target.periodType === periodType && target.periodKey === periodKey
  );

  return matched.reduce(
    (acc, target) => ({
      stripTarget: acc.stripTarget + (Number(target.stripTarget) || 0),
      amountTarget: acc.amountTarget + (Number(target.amountTarget) || 0)
    }),
    { stripTarget: 0, amountTarget: 0 }
  );
};

const sumStockMetrics = (transactions, invoices, periodType, meta) => {
  let stockInQty = 0;
  let stockInCount = 0;
  let stockOutStrips = 0;
  let stockOutSamples = 0;
  let stockOutTxnQty = 0;
  let stockOutCount = 0;
  const products = new Set();

  invoices.forEach((invoice) => {
    if (!invoiceInPeriod(invoice, periodType, meta)) return;
    const totals = computeInvoiceTotals(invoice?.products || []);
    const outStrips = totals.quantity + totals.freeQuantity;
    if (outStrips > 0) {
      stockOutStrips += outStrips;
      stockOutCount += 1;
    }
    (invoice.products || []).forEach((line) => {
      if (line.product) products.add(String(line.product).trim());
    });
  });

  transactions.forEach((txn) => {
    const txnType = txn.type || 'stock_in';

    if (txnType === 'stock_in' && stockInPeriod(txn, periodType, meta)) {
      stockInQty += Number(txn.quantity) || 0;
      stockInCount += 1;
      products.add(String(txn.materialName || '').trim());

      (txn.sampleEntries || []).forEach((entry) => {
        const entryDate = entry?.date ? new Date(entry.date) : getTransactionDate(txn);
        if (!dateInPeriod(entryDate, periodType, meta)) return;
        const sampleQty = Number(entry.quantity) || 0;
        if (sampleQty > 0) {
          stockOutSamples += sampleQty;
          stockOutCount += 1;
        }
      });
    }

    if (txnType === 'stock_out' && stockInPeriod(txn, periodType, meta)) {
      const qty = Number(txn.quantity) || 0;
      stockOutTxnQty += qty;
      stockOutCount += 1;
      products.add(String(txn.materialName || '').trim());
    }
  });

  const stockOutQty = stockOutStrips + stockOutTxnQty;
  const stockInStripsCapacity = stockInQty * DEFAULT_STRIPS_PER_BOX;
  const fulfillmentRate =
    stockInStripsCapacity > 0 ? (stockOutStrips / stockInStripsCapacity) * 100 : 0;

  return {
    stockInQty,
    stockInCount,
    stockOutQty,
    stockOutStrips,
    stockOutSamples,
    stockOutTxnQty,
    stockOutCount,
    netMovement: stockInQty - stockOutSamples,
    fulfillmentRate,
    activeProducts: products.size
  };
};

export const buildKpiSnapshot = ({
  invoices = [],
  targets = [],
  stockTransactions = [],
  expiryStats = {},
  periodType = 'monthly'
}) => {
  const meta = getPeriodMeta(periodType);
  const currentInvoices = invoices.filter((inv) => invoiceInPeriod(inv, periodType, meta));
  const previousInvoices = invoices.filter((inv) => invoiceInPreviousPeriod(inv, periodType, meta));
  const currentSales = sumInvoiceMetrics(currentInvoices);
  const previousSales = sumInvoiceMetrics(previousInvoices);
  const periodTargets = sumTargetsForPeriod(targets, periodType, meta);
  const stock = sumStockMetrics(stockTransactions, currentInvoices, periodType, meta);

  const revenueGrowth =
    previousSales.revenue > 0
      ? ((currentSales.revenue - previousSales.revenue) / previousSales.revenue) * 100
      : currentSales.revenue > 0
        ? 100
        : 0;

  const stripGrowth =
    previousSales.strips > 0
      ? ((currentSales.strips - previousSales.strips) / previousSales.strips) * 100
      : currentSales.strips > 0
        ? 100
        : 0;

  const expiryTotal = Number(expiryStats.total) || 0;
  const expiryExpired = Number(expiryStats.expired) || 0;
  const expiryExpiring = Number(expiryStats.expiring) || 0;
  const expirySafe = Math.max(expiryTotal - expiryExpired - expiryExpiring, Number(expiryStats.safe) || 0);
  const expiryCompliance = expiryTotal ? (expirySafe / expiryTotal) * 100 : 100;

  const salesKPIs = [
    {
      label: 'Total Revenue',
      value: currentSales.revenue,
      target: periodTargets.amountTarget,
      format: 'currency'
    },
    {
      label: 'Strips Sold',
      value: currentSales.strips,
      target: periodTargets.stripTarget,
      format: 'number'
    },
    {
      label: 'Invoices Generated',
      value: currentSales.invoiceCount,
      target: Math.max(previousSales.invoiceCount, 1),
      format: 'number',
      compareGrowth: true
    },
    {
      label: 'Average Order Value',
      value: currentSales.avgOrderValue,
      target: previousSales.avgOrderValue || currentSales.avgOrderValue,
      format: 'currency',
      optionalTarget: previousSales.avgOrderValue === 0
    }
  ];

  const productionKPIs = [
    {
      label: 'Stock In Quantity',
      value: stock.stockInQty,
      target: Math.max(stock.stockOutSamples, 1),
      format: 'number',
      unit: 'boxes'
    },
    {
      label: 'Stock Out — Invoices',
      value: stock.stockOutStrips,
      target: Math.max(stock.stockInQty * DEFAULT_STRIPS_PER_BOX, 1),
      format: 'number',
      unit: 'strips',
      optionalTarget: stock.stockInQty === 0
    },
    {
      label: 'Stock Out — Samples',
      value: stock.stockOutSamples,
      target: Math.max(stock.stockInQty, 1),
      format: 'number',
      unit: 'boxes',
      optionalTarget: stock.stockInQty === 0
    },
    {
      label: 'Stock Out — Total',
      value: stock.stockOutQty,
      target: Math.max(stock.stockInQty * DEFAULT_STRIPS_PER_BOX, 1),
      format: 'number',
      unit: 'strips',
      optionalTarget: stock.stockInQty === 0 && stock.stockOutTxnQty === 0
    },
    {
      label: 'Fulfillment Rate',
      value: stock.fulfillmentRate,
      target: 100,
      format: 'percent'
    }
  ];

  const complianceKPIs = [
    {
      label: 'Payment Collection Rate',
      value: currentSales.collectionRate,
      target: 100,
      format: 'percent'
    },
    {
      label: 'Expiry Compliance',
      value: expiryCompliance,
      target: 100,
      format: 'percent'
    },
    {
      label: 'Expired Stock Items',
      value: expiryExpired,
      target: 0,
      format: 'number',
      lowerIsBetter: true
    },
    {
      label: 'Invoice Documentation',
      value: currentSales.documentationRate,
      target: 100,
      format: 'percent'
    }
  ];

  return {
    meta,
    summary: {
      revenueGrowth,
      stripGrowth,
      productionEfficiency: stock.fulfillmentRate,
      stockInQty: stock.stockInQty,
      stockOutQty: stock.stockOutQty,
      stockOutStrips: stock.stockOutStrips,
      stockOutSamples: stock.stockOutSamples,
      complianceScore: Math.round(
        (currentSales.collectionRate + expiryCompliance + currentSales.documentationRate) / 3
      ),
      salesReach: calcReachPercent(currentSales.revenue, periodTargets.amountTarget),
      stripReach: calcReachPercent(currentSales.strips, periodTargets.stripTarget)
    },
    salesKPIs,
    productionKPIs,
    complianceKPIs,
    currentSales,
    periodTargets,
    stock
  };
};

export const formatKpiValue = (value, format) => {
  if (format === 'currency') {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(Number(value) || 0);
  }
  if (format === 'percent') {
    return `${(Number(value) || 0).toFixed(1)}%`;
  }
  return (Number(value) || 0).toLocaleString('en-IN');
};

export const getKpiProgress = (value, target, lowerIsBetter = false) => {
  const val = Number(value) || 0;
  const tgt = Number(target) || 0;

  if (lowerIsBetter) {
    if (tgt === 0 && val === 0) return 100;
    if (val === 0) return 100;
    if (val <= tgt) return 100;
    return Math.max(0, 100 - val * 10);
  }

  if (tgt <= 0) return val > 0 ? 100 : 0;
  return Math.min((val / tgt) * 100, 100);
};

export const getPeriodLabel = (periodType) => {
  const labels = {
    weekly: 'This Week',
    monthly: 'This Month',
    quarterly: 'This Quarter',
    yearly: 'This Year'
  };
  return labels[periodType] || 'Current Period';
};
